import { useState, useRef } from "react";
import { toast } from "sonner";
import { uploadExcel } from "@/lib/api";
import { UploadCloud, X, FileSpreadsheet, Loader2 } from "lucide-react";

export default function UploadDialog({ module, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("File harus berformat .xlsx atau .xls");
      return;
    }
    setFile(f);
  };

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadExcel(module.id, file);
      toast.success(`${res.inserted} baris berhasil diunggah`);
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal mengunggah file";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-1">
              Unggah Excel
            </div>
            <h3
              data-testid="upload-title"
              className="font-display font-black text-xl tracking-tight"
            >
              {module.name}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Data akan <b>ditambahkan</b> ke modul (append/merge).
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="upload-close"
            className="p-1.5 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-file-input"
          />

          <div
            data-testid="upload-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              drag
                ? "border-[#0047FF] bg-[#0047FF]/5"
                : "border-slate-300 hover:border-[#0047FF] hover:bg-slate-50"
            }`}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="w-10 h-10 text-[#0047FF]" />
                <div className="font-semibold text-sm">{file.name}</div>
                <div className="mono text-xs text-slate-400">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-xs text-rose-500 hover:underline mt-1"
                >
                  Pilih file lain
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <UploadCloud className="w-10 h-10 text-slate-400" />
                <div className="font-semibold text-sm">
                  Tarik dan lepas file Excel di sini
                </div>
                <div className="text-xs text-slate-500">
                  atau klik untuk memilih (.xlsx / .xls)
                </div>
              </div>
            )}
          </div>

          {/* Expected columns hint */}
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 text-xs">
            <div className="font-semibold text-slate-700 mb-1">
              Kolom yang dikenali:
            </div>
            <div className="flex flex-wrap gap-1.5">
              {module.columns.map((c) => (
                <span
                  key={c.key}
                  className="mono text-[10px] uppercase tracking-wider bg-white border border-slate-200 px-2 py-1"
                >
                  {c.label}
                </span>
              ))}
            </div>
            <div className="text-slate-500 mt-2">
              Header pada baris pertama sheet Excel akan dicocokkan otomatis.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="btn-sharp px-4 py-2 border border-slate-300 text-sm font-semibold hover:border-slate-500"
          >
            Batal
          </button>
          <button
            data-testid="upload-submit"
            disabled={!file || uploading}
            onClick={doUpload}
            className="btn-sharp inline-flex items-center gap-2 bg-[#0047FF] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0033CC] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Mengunggah...
              </>
            ) : (
              <>
                <UploadCloud className="w-4 h-4" /> Unggah
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
